/**
 * Affiliate Portal API Client
 *
 * Calls the /api/affiliate endpoints with the affiliate JWT (NOT the user JWT).
 * Token is stored in localStorage under a separate key so it never collides
 * with the main app's user token.
 */

import {
  AffiliateProfile,
  AffiliateApplicationProfile,
  AffiliateApplicationState,
  DashboardStats,
  AffiliateLink,
  AffiliateCommission,
  AffiliatePayout,
  ReferralListItem,
  DailyCount,
  AffiliateProgramInfo,
} from '../types/affiliate';

const defaultApiUrl =
  process.env.NODE_ENV === 'production' ? 'https://api.saillab.co.jp' : 'http://localhost:5000';

const API_BASE_URL = `${process.env.REACT_APP_API_URL || defaultApiUrl}/api/affiliate`;

export const AFFILIATE_TOKEN_STORAGE_KEY = 'saillab_affiliate_token';

const getToken = (): string | null => {
  try {
    return localStorage.getItem(AFFILIATE_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
};

const setToken = (token: string | null): void => {
  try {
    if (token) localStorage.setItem(AFFILIATE_TOKEN_STORAGE_KEY, token);
    else localStorage.removeItem(AFFILIATE_TOKEN_STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

class AffiliateApiError extends Error {
  status: number;
  body: any;
  constructor(message: string, status: number, body: any) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = getToken();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });
  const text = await res.text();
  let data: any = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    throw new AffiliateApiError(`api_error_${res.status}`, res.status, data);
  }
  return data as T;
}

function buildAffiliateSignInUrl(
  provider: 'google' | 'microsoft',
  campaignCode?: string | null,
  language?: string
): string {
  const base = process.env.REACT_APP_API_URL || defaultApiUrl;
  const params = new URLSearchParams();
  if (campaignCode) params.set('c', campaignCode);
  if (language) params.set('lang', language);
  const qs = params.toString();
  return `${base}/api/auth/affiliate/${provider}${qs ? `?${qs}` : ''}`;
}

export const affiliateApi = {
  // Token management
  setToken,
  getToken,
  clearToken: () => setToken(null),

  // Auth init helpers — return URLs the portal navigates to.
  // `campaignCode` comes from ?c=CODE on the signup page and is forwarded so the
  // server can enrol the new affiliate into that campaign's commission rate.
  // `language` is the portal's UI language, so a new affiliate's emails match it.
  getGoogleSignInUrl: (campaignCode?: string | null, language?: string): string =>
    buildAffiliateSignInUrl('google', campaignCode, language),
  getMicrosoftSignInUrl: (campaignCode?: string | null, language?: string): string =>
    buildAffiliateSignInUrl('microsoft', campaignCode, language),

  // Public — no token required. Drives the rate shown on the marketing page so
  // it always matches what signup would actually assign.
  getProgramInfo: (campaignCode?: string | null) =>
    call<AffiliateProgramInfo>(
      'GET',
      `/program-info${campaignCode ? `?c=${encodeURIComponent(campaignCode)}` : ''}`
    ),

  // Profile
  getMe: () => call<{ affiliate: AffiliateProfile }>('GET', '/me'),

  // Application — reachable while pending or rejected, unlike everything below
  getApplication: () => call<AffiliateApplicationState>('GET', '/application'),
  submitApplication: (application: AffiliateApplicationProfile) =>
    call<{
      status: AffiliateApplicationState['status'];
      application: AffiliateApplicationProfile;
      submittedAt: string;
      autoApproved: boolean;
    }>('PUT', '/application', { application }),

  // Dashboard
  getStats: () => call<DashboardStats>('GET', '/dashboard/stats'),
  getTimeseries: (days = 30) =>
    call<{
      days: number;
      clicksByDay: DailyCount[];
      signupsByDay: DailyCount[];
      conversionsByDay: DailyCount[];
    }>('GET', `/dashboard/timeseries?days=${days}`),

  // Referrals
  getRecentReferrals: (limit = 20) =>
    call<{ referrals: ReferralListItem[] }>('GET', `/referrals?limit=${limit}`),

  // Commissions
  getCommissions: (opts: { limit?: number; offset?: number; status?: string } = {}) => {
    const params = new URLSearchParams();
    if (opts.limit !== undefined) params.set('limit', String(opts.limit));
    if (opts.offset !== undefined) params.set('offset', String(opts.offset));
    if (opts.status) params.set('status', opts.status);
    const qs = params.toString();
    return call<{ commissions: AffiliateCommission[]; total: number }>(
      'GET',
      `/commissions${qs ? `?${qs}` : ''}`
    );
  },
  getCommissionsCsvUrl: (status?: string): string => {
    const base = `${API_BASE_URL}/commissions.csv`;
    return status ? `${base}?status=${encodeURIComponent(status)}` : base;
  },

  // Payouts
  getPayouts: () => call<{ payouts: AffiliatePayout[] }>('GET', '/payouts'),

  // Links
  listLinks: () => call<{ links: AffiliateLink[] }>('GET', '/links'),
  createLink: (input: { slug?: string; name?: string; landingPath?: string }) =>
    call<{ link: AffiliateLink }>('POST', '/links', input),
  updateLink: (
    id: number,
    input: { slug?: string | null; name?: string | null; landingPath?: string; disabled?: boolean }
  ) => call<{ link: AffiliateLink }>('PATCH', `/links/${id}`, input),
};

export { AffiliateApiError };
export default affiliateApi;
